'use client';

import { useTranslation } from 'react-i18next';
import { weekdayLabels } from '../baseline/baseline-days';
import type { BaselineWording } from '../baseline/baseline-wording';
import type { WeekSummaryData } from '../baseline/baseline-week';
import { ensureBaselineStyle } from '../baseline/baseline-style';

export interface WeekSummaryProps {
  summary: WeekSummaryData;
  wording: BaselineWording;
  /** A yes/no week: the tiles count days, and each day reads Yes or No. */
  yesNo?: boolean;
}

const fillFor = (count: number) => (count > 0 ? 'use' : 'free');

/**
 * v9 week summary: the week's total and its days with none, then one cell per
 * day Monday to Sunday, filled by whether the day had any. Read-only; each
 * cell is named in full for screen readers: the weekday, the count and the
 * wording's unit, or for a yes/no week whether the day was used.
 */
export function WeekSummary({ summary, wording, yesNo = false }: WeekSummaryProps) {
  ensureBaselineStyle();
  const { t, i18n } = useTranslation('signup');
  return (
    <div className="signup-baseline__summary" data-testid="signup-baseline-typical-summary">
      <div className="signup-baseline__tiles">
        <p className="signup-baseline__tile" data-testid="signup-baseline-typical-total">
          <span className="signup-baseline__badge" data-fill="use">
            {summary.total}
          </span>
          <span>{wording.totalLabel}</span>
        </p>
        <p className="signup-baseline__tile" data-testid="signup-baseline-typical-free-days">
          <span className="signup-baseline__badge" data-fill="free">
            {summary.freeDays}
          </span>
          <span>{wording.freeLabel}</span>
        </p>
      </div>
      <p className="signup-baseline__summary-label">{t('baseline.typical.basedOn')}</p>
      <ul className="signup-baseline__cells">
        {summary.cells.map(({ weekday, count }) => {
          const labels = weekdayLabels(weekday, i18n.language);
          // English day names keep the test ids stable whatever the language.
          const testKey = weekdayLabels(weekday).short.toLowerCase();
          return (
            <li
              key={weekday}
              className="signup-baseline__cell"
              data-testid={`signup-baseline-typical-day-${testKey}`}
            >
              <span aria-hidden="true">{labels.short}</span>
              <span
                className="signup-baseline__cell-count"
                data-fill={fillFor(count)}
                aria-hidden="true"
              >
                {yesNo ? t(count > 0 ? 'baseline.yesNo.yes' : 'baseline.yesNo.no') : count}
              </span>
              <span className="signup-visually-hidden">
                {yesNo
                  ? t('baseline.yesNo.dayCell', {
                      weekday: labels.long,
                      answer: t(count > 0 ? 'baseline.yesNo.used' : 'baseline.yesNo.notUsed'),
                    })
                  : t('baseline.typical.dayCell', {
                      weekday: labels.long,
                      count,
                      unit: count === 1 ? wording.unit.one : wording.unit.other,
                    })}
              </span>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
